import { trpc, RouterOutputs } from '@org/clients/school';
import {
  Badge,
  Button,
  ConfirmDialog,
  DatePicker,
  Skeleton,
  Switch,
  unwrapTrpcData,
} from '@org/ui';
import { endOfDay, format } from 'date-fns';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { getMutationErrorMessage } from '../shared/multi-step-utils';

type PendingRequest = RouterOutputs['journeyAccess']['listPendingRequests'][number];

type PendingRequestsPanelProps = {
  /** When set, only this student's requests are shown (Access dialog). */
  userId?: string;
  /** Render nothing at all when there are no pending requests. */
  hideWhenEmpty?: boolean;
  /** Hide the student column (already obvious inside the Access dialog). */
  hideStudent?: boolean;
  /** Optional heading rendered above the list (hidden with it when empty). */
  title?: string;
};

// Same three choices as the grant form: use the product's default duration
// (undefined), pick an end date (Date) or never expire (null).
type ApproveEditor = {
  purchaseId: string;
  useDefault: boolean;
  never: boolean;
  endsAt: Date | undefined;
};

const toDate = (value: unknown): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value as string);
  return Number.isNaN(date.getTime()) ? null : date;
};

export function usePendingRequests(userId?: string) {
  const query = trpc.journeyAccess.listPendingRequests.useQuery(userId ? { userId } : {});
  const rows = unwrapTrpcData<PendingRequest[]>(query.data as PendingRequest[]) ?? [];
  return { ...query, rows };
}

export function PendingRequestsPanel({
  userId,
  hideWhenEmpty = false,
  hideStudent = false,
  title,
}: PendingRequestsPanelProps) {
  const { t } = useTranslation('admin');
  const utils = trpc.useUtils();
  const { rows, isLoading } = usePendingRequests(userId);

  const [editor, setEditor] = useState<ApproveEditor | null>(null);
  const [pendingDecline, setPendingDecline] = useState<PendingRequest | null>(null);

  const invalidate = async (request: PendingRequest) => {
    await utils.journeyAccess.listPendingRequests.invalidate();
    await utils.journeyAccess.listForUser.invalidate({ userId: request.userId });
    await utils.journeys.getUserJourneys.invalidate({ userId: request.userId, activeOnly: false });
    await utils.progress.getActiveJourney.invalidate({ userId: request.userId });
    await utils.products.list.invalidate();
  };

  const approveMutation = trpc.journeyAccess.approveRequest.useMutation({
    onError: (error: unknown) =>
      toast.error(t('pendingRequests.toasts.approveError', { message: getMutationErrorMessage(error) })),
  });

  const declineMutation = trpc.journeyAccess.declineRequest.useMutation({
    onError: (error: unknown) =>
      toast.error(t('pendingRequests.toasts.declineError', { message: getMutationErrorMessage(error) })),
  });

  const handleApprove = async (request: PendingRequest) => {
    if (!editor || editor.purchaseId !== request.purchaseId) return;
    if (!editor.useDefault && !editor.never && !editor.endsAt) {
      toast.error(t('journeyAccess.dialog.toasts.pickDate'));
      return;
    }
    const endsAt = editor.useDefault
      ? undefined
      : editor.never
        ? null
        : endOfDay(editor.endsAt as Date);
    try {
      await approveMutation.mutateAsync({ purchaseId: request.purchaseId, endsAt });
      toast.success(t('pendingRequests.toasts.approveSuccess', { name: request.studentName ?? '' }));
      setEditor(null);
      await invalidate(request);
    } catch {
      // onError already reported it.
    }
  };

  const confirmDecline = async () => {
    if (!pendingDecline) return;
    const request = pendingDecline;
    setPendingDecline(null);
    try {
      await declineMutation.mutateAsync({ purchaseId: request.purchaseId });
      toast.success(t('pendingRequests.toasts.declineSuccess'));
      await invalidate(request);
    } catch {
      // onError already reported it.
    }
  };

  const formatDate = (value: unknown) => {
    const date = toDate(value);
    return date ? format(date, 'PP') : '';
  };

  if (hideWhenEmpty && !isLoading && rows.length === 0) {
    return null;
  }

  return (
    <div className="mt-2 flex flex-col gap-3">
      {title ? <h3 className="font-medium text-foreground border-b pb-2">{title}</h3> : null}
      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">{t('pendingRequests.empty')}</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {rows.map((request) => {
            const current = editor && editor.purchaseId === request.purchaseId ? editor : null;
            const busy = approveMutation.isPending || declineMutation.isPending;
            return (
              <li
                key={request.purchaseId}
                className="rounded-lg border border-dashed border-amber-400 bg-amber-50/40 p-3"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{request.productName}</span>
                      <Badge variant="outline">{t('pendingRequests.badge')}</Badge>
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {[request.journeyName, request.levelName].filter(Boolean).join(' · ')}
                    </span>
                    {!hideStudent && (
                      <span className="text-sm">
                        {request.studentName || t('pendingRequests.unnamedStudent')}
                        {request.studentEmail ? (
                          <span className="text-muted-foreground"> · {request.studentEmail}</span>
                        ) : null}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {t('pendingRequests.requestedAt', { date: formatDate(request.requestedAt) })}
                    </span>
                    {request.note ? (
                      <p className="mt-1 text-sm italic text-muted-foreground">“{request.note}”</p>
                    ) : null}
                  </div>

                  {current ? (
                    <div className="flex min-w-[240px] flex-col gap-2">
                      <label className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Switch
                          checked={current.useDefault}
                          onCheckedChange={(checked) => setEditor({ ...current, useDefault: checked })}
                        />
                        {t('journeyAccess.dialog.grant.useDefault')}
                      </label>
                      {!current.useDefault && (
                        <>
                          <DatePicker
                            value={current.endsAt}
                            onChange={(date) => setEditor({ ...current, endsAt: date })}
                            placeholder={t('journeyAccess.dialog.actions.pickEndDate')}
                            disabled={current.never}
                          />
                          <label className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Switch
                              checked={current.never}
                              onCheckedChange={(checked) => setEditor({ ...current, never: checked })}
                            />
                            {t('journeyAccess.dialog.neverExpires')}
                          </label>
                        </>
                      )}
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleApprove(request)}
                          disabled={!current.useDefault && !current.never && !current.endsAt}
                          isLoading={approveMutation.isPending}
                          loadingText={t('common.status.saving')}
                        >
                          {t('pendingRequests.actions.confirmApprove')}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditor(null)}
                          disabled={approveMutation.isPending}
                        >
                          {t('journeyAccess.dialog.actions.cancel')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() =>
                          setEditor({
                            purchaseId: request.purchaseId,
                            useDefault: true,
                            never: false,
                            endsAt: undefined,
                          })
                        }
                      >
                        {t('pendingRequests.actions.approve')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-muted-foreground hover:text-red-600"
                        disabled={busy}
                        onClick={() => setPendingDecline(request)}
                      >
                        {t('pendingRequests.actions.decline')}
                      </Button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <ConfirmDialog
        open={pendingDecline !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDecline(null);
        }}
        description={t('pendingRequests.actions.declineConfirm', {
          name: pendingDecline?.studentName ?? '',
          product: pendingDecline?.productName ?? '',
        })}
        confirmLabel={t('pendingRequests.actions.decline')}
        destructive
        onConfirm={confirmDecline}
      />
    </div>
  );
}
